import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import dayjs from 'dayjs'
import { Flex, List, WhiteSpace, WingBlank } from 'antd-mobile'
import { Toast } from 'antd-mobile-v5'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { pageLoaded, pageLoading } from '../../actions'
import { formatDate, formatMoney, showAlert } from '../../components/common/utils'
import { agentElearningOrder, agentElearningSubmit } from '../../services/api'
import InvoiceInfo from '../PriceList/InvoiceInfo'
import { useAgentElearningPaymentStatus, useAgentElearningCloseOrder, useGetInvoice } from '../../services/hooks'
import FuiouQrPopup from '../../components/FormTools/FuiouQrPopup'
import {
  createWposPayTypePicker,
  FUIOU_H5_PAY_TYPE,
  isValidWposQrContent,
  pickFuiouChannel,
  safeFuiouRedirect,
} from '../../components/FormTools/utils'
import { getWechatOpenId } from '../../utils/wechat'

import SearchStudent from './SearchStudent'
import PriceItem from './PriceItem'
import CheckoutCounter from './CheckoutCounter'

const getSubmitItems = (items, inCart) =>
  items.filter((item) => inCart.indexOf(item.skuId) > -1).map((item) => ({ ...item, number: 1 }))

export const getTotal = (result, inCart) => {
  const price = result
    ? result.skus
        .filter((item) => inCart.indexOf(item.skuId) > -1)
        .reduce((sum, item) => (item.discountPrice != null ? item.discountPrice : item.price) - 0 + sum, 0)
    : 0
  const currency = result && result.skus && result.skus[0] ? result.skus[0].currencyType : ''
  return formatMoney(price, currency, true)
}

const ElearningList = ({ result, inCart, toggleSelect }) => {
  if (!result)
    return (
      <WingBlank>
        <WhiteSpace />
        <WhiteSpace />
        <p>
          <FormattedMessage id="agent-elearning-email-hint" />
        </p>
        <WhiteSpace />
      </WingBlank>
    )
  const { skus } = result
  if (skus.length === 0)
    return (
      <WingBlank>
        <WhiteSpace />
        <FormattedMessage id="agent-elearning-not-available" />
        <WhiteSpace />
      </WingBlank>
    )
  return skus.map((item) => (
    <PriceItem item={item} key={item.skuId} toggleSelect={toggleSelect} checked={inCart.indexOf(item.skuId) > -1} />
  ))
}
export default () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { state } = useLocation()
  const { submitData, searchResult: oldSearchResult } = state || {}
  const { formatMessage } = useIntl()
  const [email, setEmail] = useState(submitData && submitData.email)
  if (submitData && submitData.profile) {
    submitData.profile.birthDate = dayjs(submitData.profile.birthDate).format('YYYY-MM-DD')
  }
  const [searchResult, setSearchResult] = useState(oldSearchResult)

  const [inCart, setInCart] = useState([])
  const [profile, setProfile] = useState((submitData && submitData.profile) || {})
  const [invoiceInfo, setInvoiceInfo] = useState()
  const [showQr, setShowQr] = useState(false)
  const [qrUrl, setQrUrl] = useState('')
  // 应付金额（CHNA-2618：ELearning 建单响应补齐 price/currency，反算自分值、与实扣一致）
  const [qrPrice, setQrPrice] = useState(null)
  const [qrCurrency, setQrCurrency] = useState(null)
  const [orderCode, setOrderCode] = useState()
  // 支付成功持久状态：Toast 消失后页面仍需可见的成功标识
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  // WPOS 渠道选择结果（WECHAT/ALIPAY），传给 FuiouQrPopup 展示对应渠道边框色与文案
  const [qrPayType, setQrPayType] = useState(null)
  // 渠道选择面板等待期间禁用结算按钮（loading 此时恒为 false）
  const [pickingChannel, setPickingChannel] = useState(false)
  const wposPickerRef = useRef(null)
  // 互斥锁：防双击结算并发建单（与 EcardCheckoutPopup/BooksCheckout 一致）
  const payingRef = useRef(false)
  // await picker 后组件可能已卸载：setState 前判 mounted 防卸载告警
  const mountedRef = useRef(true)
  const { profile: mainProfile } = useSelector((state) => ({
    profile: state.data.profile,
  }))
  const { loading: invoiceLoading } = useGetInvoice({
    onSuccess: (data) => {
      setInvoiceInfo(data)
    },
  })

  // WPOS 扫码后轮询支付结果（hooks 层返回裸 body；后端确认 2026-08-16 端点终态门控，
  // 仅 status=PAID/TRADE_PAID 时返回非 null paymentMethod，统一按 status 判定）
  const { runAsync: checkPaymentStatus } = useAgentElearningPaymentStatus({ manual: true, onError: () => true })
  // 关单只由用户显式「取消订单」触发（弹窗关闭/超时都不再关单，见 FuiouQrPopup）：
  // 失败向上抛，由弹窗静默收起——后端幂等，失败时订单仍是 WAIT_PAY，可再次发起支付
  const { runAsync: closeAgentElearningOrder } = useAgentElearningCloseOrder({ manual: true, onError: () => true })
  const cancelOrder = useCallback(async () => {
    if (!orderCode) return
    await closeAgentElearningOrder(orderCode)
  }, [closeAgentElearningOrder, orderCode])

  const checkStatus = useCallback(async () => {
    const result = await checkPaymentStatus(orderCode)
    // TRADE_PAID（富友支付成功终态）与 PAID 等价：漏判会把已付款订单误判为未支付，
    // 诱导用户重复下单付款（二次扣款）
    return !!(result && ['PAID', 'TRADE_PAID'].includes(result.status))
  }, [checkPaymentStatus, orderCode])

  const handlePaymentSuccess = useCallback(() => {
    setShowQr(false)
    setPaymentSuccess(true)
    Toast.show({
      icon: 'success',
      content: formatMessage({ id: 'fuiou-pay-success' }),
      // 支付成功后不留在代购页：本页无"已支付"凭证（刷新即丢），跳通用成功页
      // 携带真实订单号与商品清单，确认闭环成立（与 BooksCheckout WPOS 一致）
      afterClose: () =>
        history.replace('/pay_success', {
          payResult: {
            id: orderCode || '-',
            // 成功页商品契约 { name, unitPrice }：代购 sku 字段为 title/discountPrice，需映射
            items: getSubmitItems(searchResult.skus, inCart).map((sku) => ({
              name: sku.title,
              unitPrice: sku.discountPrice != null ? sku.discountPrice : sku.price,
            })),
            price: qrPrice != null ? qrPrice : 0,
            currencyType: qrCurrency || (searchResult.skus[0] && searchResult.skus[0].currencyType),
          },
        }),
    })
  }, [formatMessage, history, orderCode, searchResult, inCart, qrPrice, qrCurrency])

  // 组件卸载时清理残留的命令式渠道选择面板
  useEffect(() => {
    // StrictMode 下 effect 会 setup→cleanup→setup 重跑：cleanup 置 false 后必须复位，
    // 否则重挂载后 mountedRef 恒 false，支付回调全部失效
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (wposPickerRef.current) {
        wposPickerRef.current.close()
        wposPickerRef.current = null
      }
    }
  }, [])

  const toggleSelect = (id, checked) => {
    if (checked) {
      setInCart([...inCart, id])
    } else {
      setInCart(inCart.filter((c) => c !== id))
    }
  }

  const toCheckout = async () => {
    if (payingRef.current) return
    if (!searchResult || inCart.length === 0) return
    if (!searchResult.userExists) {
      if (!profile.firstName) {
        showAlert(formatMessage({ id: 'setProfile-errorName' }), formatMessage({ id: 'common-close' }))
        return
      } else if (!profile.lastName) {
        showAlert(formatMessage({ id: 'setProfile-errorName' }), formatMessage({ id: 'common-close' }))
        return
      } else if (!profile.gender) {
        showAlert(formatMessage({ id: 'setProfile-errorGender' }), formatMessage({ id: 'common-close' }))
        return
      } else if (!profile.birthDate) {
        showAlert(formatMessage({ id: 'setProfile-errorBirthDate' }), formatMessage({ id: 'common-close' }))
        return
      }
    }

    payingRef.current = true
    try {
      const submitData = {
        email,
        skus: getSubmitItems(searchResult.skus, inCart),
      }
      if (profile.firstName && profile.lastName && profile.birthDate && profile.gender) {
        submitData.profile = {
          ...profile,
          birthDate: formatDate(profile.birthDate, 'MM/DD/YYYY'),
        }
      }
      const channel = searchResult.mainland ? pickFuiouChannel() : null
      let payType = channel === 'H5PAY' ? FUIOU_H5_PAY_TYPE.WECHAT_JSAPI : undefined
      // 后端要求 WPOS 显式传 payType（FAPPLET 聚合码已下线），下单前先弹渠道选择
      if (channel === 'WPOS') {
        const picker = createWposPayTypePicker(formatMessage)
        wposPickerRef.current = picker
        setPickingChannel(true)
        try {
          payType = await picker.promise
        } finally {
          if (mountedRef.current) setPickingChannel(false)
          wposPickerRef.current = null
        }
        // 用户取消渠道选择，静默返回
        if (!payType) return
        if (mountedRef.current) setQrPayType(payType)
      }
      try {
        dispatch(pageLoading())
        if (searchResult.mainland) {
          const { data } = await agentElearningSubmit({ ...submitData, channel, payType, openid: getWechatOpenId() })
          if (data && data.url) {
            if (channel === 'H5PAY') {
              safeFuiouRedirect(data.url, formatMessage({ id: 'fuiou-pay-url-invalid' }))
            } else if (!isValidWposQrContent(data.url)) {
              // WPOS 二维码不经 safeFuiouRedirect，此处单独过二维码内容白名单（单码形态，兼容富友聚合链接）
              Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-url-invalid' }) })
            } else if (!data.code) {
              // code 缺失无法启动轮询，直接提示失败，避免二维码永挂零反馈
              Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-fail' }) })
            } else {
              setQrUrl(data.url)
              setQrPrice(data.price)
              setQrCurrency(data.currency)
              setOrderCode(data.code)
              setShowQr(true)
            }
          } else {
            // 下单成功但无 url（异常形态/免费单）：提示失败，避免静默无反馈
            Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-fail' }) })
          }
        } else {
          const subData = { ...submitData, invoiceDTO: invoiceInfo }
          const { data: order } = await agentElearningOrder(subData)
          history.push('/agent_elearning_checkout', { order, submitData: subData, searchResult })
        }
      } catch (e) {
        // 下单失败给用户可见提示（原 console.log + throw 会产生未处理 rejection）
        Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-fail' }) })
      } finally {
        dispatch(pageLoaded())
      }
    } finally {
      payingRef.current = false
    }
  }

  return (
    <Flex direction="column" justify="between" align="stretch" style={{ width: '100%', height: '100%' }}>
      <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <div style={{ width: '100%', height: '100%', overflowY: 'scroll' }}>
          <List renderHeader={formatMessage({ id: 'agent-elearning-header-email' })}>
            <SearchStudent
              email={submitData && submitData.email}
              text={formatMessage({ id: 'common-email' })}
              placeholder={formatMessage({ id: 'agent-elearning-email-placeholder' })}
              editable
              profile={profile}
              setProfile={setProfile}
              onSuccess={(email, searchResult) => {
                setEmail(email)
                setInCart([])
                setSearchResult(searchResult)
              }}
              searchResult={searchResult}
            />
          </List>
          {!invoiceLoading && mainProfile && mainProfile.teachingLocation === 'TW' && (
            <InvoiceInfo invoiceInfo={invoiceInfo} />
          )}
          <List
            renderHeader={
              <>
                {formatMessage({ id: 'agent-elearning-header-list' })}
                {searchResult?.level ? (
                  <span style={{ marginLeft: 12, color: '#29489E', fontSize: '0.8rem' }}>({searchResult.level})</span>
                ) : null}
              </>
            }
          >
            <ElearningList result={searchResult} inCart={inCart} toggleSelect={toggleSelect} />
          </List>
        </div>
      </div>

      {paymentSuccess && (
        <p style={{ textAlign: 'center', color: '#00b578', margin: '8px 0' }}>
          <FormattedMessage id="fuiou-pay-success" />
        </p>
      )}
      <CheckoutCounter
        disabled={
          paymentSuccess ||
          inCart.length === 0 ||
          (mainProfile && mainProfile.teachingLocation === 'TW' && !invoiceInfo) ||
          pickingChannel
        }
        toCheckout={toCheckout}
        amount={inCart.length}
        total={getTotal(searchResult, inCart)}
      />
      <FuiouQrPopup
        visible={showQr}
        url={qrUrl}
        price={qrPrice}
        currency={qrCurrency}
        onClose={() => setShowQr(false)}
        onPaymentSuccess={handlePaymentSuccess}
        checkStatus={checkStatus}
        payType={qrPayType}
        onCancelOrder={cancelOrder}
      />
    </Flex>
  )
}
